import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@saiens/shared/hooks';
import { useAuthStore } from '@saiens/shared/stores';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { User, AlertCircle } from 'lucide-react';

export function BindingPage() {
  const navigate = useNavigate();
  const { status, bind, isBinding, bindError, logout } = useAuth();
  const { lineProfile } = useAuthStore();
  const [employeeName, setEmployeeName] = useState('');

  useEffect(() => {
    if (status === 'authenticated') {
      navigate('/', { replace: true });
    } else if (status === 'unauthenticated') {
      navigate('/login', { replace: true });
    }
  }, [status, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!employeeName.trim()) return;

    bind({
      employee_name: employeeName.trim(),
      line_display_name: lineProfile?.displayName || '',
      line_picture_url: lineProfile?.pictureUrl,
    });
  };

  if (status !== 'not_bound') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-white px-4">
      <div className="w-full max-w-sm">
        {/* User Avatar */}
        <div className="mb-8 text-center">
          {lineProfile?.pictureUrl ? (
            <img
              src={lineProfile.pictureUrl}
              alt={lineProfile.displayName}
              className="mx-auto h-20 w-20 rounded-full border-4 border-white shadow-lg"
            />
          ) : (
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-gray-200">
              <User className="h-10 w-10 text-gray-400" />
            </div>
          )}
          <h2 className="mt-4 text-lg font-semibold text-gray-900">
            {lineProfile?.displayName || '使用者'}
          </h2>
          <p className="text-sm text-gray-500">請綁定您的員工帳號</p>
        </div>

        {/* Binding Form */}
        <div className="rounded-2xl bg-white p-6 shadow-xl">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="employeeName" className="mb-1 block text-sm font-medium text-gray-700">
                員工姓名
              </label>
              <input
                id="employeeName"
                type="text"
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                placeholder="請輸入您的姓名"
                className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                disabled={isBinding}
              />
            </div>

            {/* Error message */}
            {bindError && (
              <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{bindError.message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isBinding || !employeeName.trim()}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3 font-medium text-white transition-colors hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {isBinding ? (
                <>
                  <LoadingSpinner size="sm" />
                  綁定中...
                </>
              ) : (
                '綁定帳號'
              )}
            </button>
          </form>

          <div className="mt-4 border-t pt-4">
            <button
              onClick={logout}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              使用其他帳號登入
            </button>
          </div>
        </div>

        <p className="mt-6 text-center text-xs text-gray-400">
          請輸入您在公司登記的姓名進行綁定
        </p>
      </div>
    </div>
  );
}
